/**
 * useVariantEditor
 * ----------------
 * Schema-agnostic engine behind an RHF + A/B-variant bundle editor
 * (extracted from VolumeDiscount's useBundleEditor so any bundle type can
 * reuse the same variant-cache/A-B machinery, not just its input widgets).
 *
 * Architecture highlights:
 *  - ONE useForm instance (React Hook Form), driven by a caller-supplied resolver.
 *  - ONE variant cache: a map of { [targetId]: complete TDoc }.
 *  - Never edits overrides directly – overrides are generated only on save.
 *  - Switching variants saves current form values into cache first, then resets.
 *  - Switching is blocked if current form has validation errors.
 *  - AB logic lives exclusively in this hook; form fields are unaware.
 *  - `skipKeys` (e.g. products, general_settings) always come from base.
 *
 * ---------------------------------------------------------------------------
 * AB-enabled UX model (IMPORTANT — read before touching this file)
 * ---------------------------------------------------------------------------
 *  - When AB is DISABLED: `activeTarget` is always BASE_TARGET. Behaves
 *    exactly like a plain form — user edits Base directly.
 *
 *  - When AB is ENABLED: BASE_TARGET is hidden from the UI entirely.
 *    `activeTarget` will never be BASE_TARGET while `abEnabled` is true —
 *    `switchVariant` actively rejects attempts to select it. The base
 *    document still exists internally (it's required to diff variants into
 *    `overrides` for the payload), it's just never shown or directly edited.
 *
 *  - AB testing requires a MINIMUM of 2 variants at all times. `enableAB`
 *    seeds Variant A and Variant B together, and `removeVariant` refuses to
 *    drop below 2. The UI should hide the remove (×) control on variant tabs
 *    whenever `variants.length <= 2`.
 *
 *  - Variant A (i.e. `variants[0]`) is special: it acts as the stand-in for
 *    "Base" from the user's point of view.
 *      - The user edits Variant A the same way they used to edit Base.
 *      - Variant A is diffed into `overrides` just like every other variant
 *        (B, C, ...) when the payload is built — it is NOT the source of
 *        truth internally, only the primary UI surface.
 *      - `skipKeys` are the one exception: whenever Variant A is flushed to
 *        cache (on switch, save, or payload build), its skip-key values are
 *        written THROUGH to the hidden base document. This is the reverse
 *        of the normal direction.
 *      - All OTHER variants (B, C, ...) keep the original behavior: their
 *        skip-key fields are overwritten FROM the hidden base document
 *        whenever they become active, so they always mirror Variant A's
 *        (i.e. base's) skip-key values and can't drift from them.
 *
 * Any TDoc used with this engine is expected to carry an `ab_config` field
 * shaped like `AbConfig` below (this is the one structural assumption the
 * generic engine makes about the document).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { nanoid } from "nanoid";
import { buildVariantValues, buildOverrides, diffObjects, type DeepPartial } from "../utilities/variantMerge";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export const BASE_TARGET = "base" as const;

export interface EditorVariant {
    id: string; // matches cache key
}

export interface AbConfigVariant {
    id: string;
    status: "active" | string;
    overrides?: Record<string, unknown>;
}

export interface AbConfig {
    enable: 0 | 1;
    exp_id?: string;
    traffic_allocation_type?: "even" | "custom";
    traffic_percentage?: Record<string, number>;
    variants?: AbConfigVariant[];
}

export interface UseVariantEditorOptions<TDoc extends { ab_config?: AbConfig }> {
    initialValues: TDoc;
    /** Resolver for the RHF form, e.g. `zodResolver(mySchema)`. */
    resolver: Resolver<TDoc>;
    /** Top-level keys that are ALWAYS taken from base/Variant A, regardless of active target. */
    skipKeys: (keyof TDoc)[];
    /**
     * Called when the user clicks "Save".
     * Receives the complete payload ready to send to the backend.
     */
    onSave: (payload: TDoc) => Promise<void>;
}

// Cache stores COMPLETE documents keyed by target id ("base" | variantId)
type VariantCache<TDoc> = Map<string, TDoc>;

// ---------------------------------------------------------------------------
// Helpers (module-level, no hook dependencies)
// ---------------------------------------------------------------------------

/** Derive the initial active target from a raw config's ab_config. */
function deriveInitialTarget<TDoc extends { ab_config?: AbConfig }>(config: TDoc): string {
    const abCfg = config?.ab_config;
    if (abCfg?.enable === 1 && abCfg.variants?.length) {
        // Variant A (first variant) is the primary editing surface once AB is on.
        return abCfg.variants[0].id;
    }
    return BASE_TARGET;
}

/**
 * Diff two values the way RHF's OWN internal deep-equal (used to compute
 * `formState.isDirty`) actually compares them — which is stricter than a
 * plain bracket-access diff. RHF's equal-check does `Object.keys(a).length
 * !== Object.keys(b).length` and `!(key in obj)` — so a key that's PRESENT
 * with value `undefined` on one side and genuinely ABSENT on the other
 * counts as a difference, even though `a[key] === b[key] === undefined`
 * bracket-access-wise. That's the exact class of divergence `diffObjects`
 * (bracket-access based, from utilities/variantMerge) can miss — e.g. it'll
 * report `isDirtyDiff: undefined` even while RHF's `isDirty` is `true`,
 * because some component's `setValue`/`register` materialized a key on the
 * live side that was never in `defaultValues` (or vice versa).
 *
 * Returns `{ path: { defaultValue, currentValue } }` for every path where
 * either the key sets or the values differ.
 */
function diffAgainstDefaultValues(
    defaultValues: unknown,
    currentValues: unknown,
    prefix = "",
    out: Record<string, { defaultValue: unknown; currentValue: unknown }> = {},
): Record<string, { defaultValue: unknown; currentValue: unknown }> {
    const isObj = (v: unknown): v is Record<string, unknown> =>
        !!v && typeof v === "object" && !Array.isArray(v) && !(v instanceof Date);

    if (isObj(defaultValues) && isObj(currentValues)) {
        const defKeys = Object.keys(defaultValues);
        const curKeys = Object.keys(currentValues);
        const allKeys = new Set([...defKeys, ...curKeys]);
        for (const key of allKeys) {
            const path = prefix ? `${prefix}.${key}` : key;
            const inDef = key in defaultValues;
            const inCur = key in currentValues;
            if (inDef !== inCur) {
                out[path] = {
                    defaultValue: inDef ? defaultValues[key] : "<key absent>",
                    currentValue: inCur ? currentValues[key] : "<key absent>",
                };
                continue;
            }
            diffAgainstDefaultValues(defaultValues[key], currentValues[key], path, out);
        }
        return out;
    }

    if (Array.isArray(defaultValues) && Array.isArray(currentValues)) {
        const maxLen = Math.max(defaultValues.length, currentValues.length);
        for (let i = 0; i < maxLen; i++) {
            diffAgainstDefaultValues(defaultValues[i], currentValues[i], prefix ? `${prefix}.${i}` : `${i}`, out);
        }
        return out;
    }

    const bothDates = defaultValues instanceof Date && currentValues instanceof Date;
    const equal = bothDates
        ? defaultValues.getTime() === currentValues.getTime()
        : Object.is(defaultValues, currentValues);
    if (!equal) {
        out[prefix || "(root)"] = { defaultValue: defaultValues, currentValue: currentValues };
    }
    return out;
}

/**
 * Flatten an RHF `FieldErrors` object (nested per the shape of TDoc) into
 * `{ "path.to.field": message }`. RHF errors are only "flat" for top-level
 * fields — anything nested (objects, arrays) keeps nesting until a leaf
 * `{ type, message }` node is hit, so a plain `Object.keys(errors)` loses
 * every error below the top level.
 */
function flattenFieldErrors(errors: unknown, prefix = ""): Record<string, string> {
    const out: Record<string, string> = {};
    if (!errors || typeof errors !== "object") return out;

    for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
        if (!value || typeof value !== "object") continue;
        const path = prefix ? `${prefix}.${key}` : key;
        const node = value as { message?: unknown; type?: unknown };
        if (typeof node.message === "string" || typeof node.type === "string") {
            out[path] = typeof node.message === "string" && node.message ? node.message : `${path} is invalid`;
        } else {
            Object.assign(out, flattenFieldErrors(value, path));
        }
    }
    return out;
}

const recalculateTraffic = (
    allocationType: "even" | "custom",
    currentVariants: { id: string }[],
    currentPercentages: Record<string, number>,
): Record<string, number> => {
    const N = currentVariants.length;
    if (N === 0) return {};

    if (allocationType === "even") {
        const pct = Math.floor(100 / N);
        const remainder = 100 - pct * N;
        const newPct: Record<string, number> = {};
        currentVariants.forEach((v, index) => {
            newPct[v.id] = pct + (index === N - 1 ? remainder : 0);
        });
        return newPct;
    } else {
        const newPct: Record<string, number> = {};
        let sum = 0;
        const missingVariants: string[] = [];

        currentVariants.forEach((v) => {
            if (typeof currentPercentages[v.id] === "number") {
                newPct[v.id] = currentPercentages[v.id];
                sum += currentPercentages[v.id];
            } else {
                missingVariants.push(v.id);
            }
        });

        if (missingVariants.length > 0) {
            const remaining = Math.max(0, 100 - sum);
            const share = Math.floor(remaining / missingVariants.length);
            const rem = remaining - share * missingVariants.length;
            missingVariants.forEach((vid, idx) => {
                newPct[vid] = share + (idx === missingVariants.length - 1 ? rem : 0);
            });
        } else if (sum !== 100 && sum > 0) {
            currentVariants.forEach((v) => {
                newPct[v.id] = Math.round((newPct[v.id] / sum) * 100);
            });
            let newSum = 0;
            currentVariants.forEach((v, idx) => {
                if (idx < N - 1) newSum += newPct[v.id];
            });
            newPct[currentVariants[N - 1].id] = 100 - newSum;
        } else if (sum === 0) {
            return recalculateTraffic("even", currentVariants, currentPercentages);
        }
        return newPct;
    }
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useVariantEditor<TDoc extends object & { ab_config?: AbConfig }>({
    initialValues,
    resolver,
    skipKeys,
    onSave,
}: UseVariantEditorOptions<TDoc>) {
    // ------------------------------------------------------------------------
    // Variant registry (AB variants list, not the cache). Never includes Base.
    // ------------------------------------------------------------------------
    const [abEnabled, setAbEnabled] = useState<boolean>(() => initialValues.ab_config?.enable === 1);
    const [variants, setVariants] = useState<EditorVariant[]>(() => {
        const abCfg = initialValues.ab_config;
        if (!abCfg?.variants?.length) return [];
        return abCfg.variants.map((v) => ({ id: v.id }));
    });

    // While AB is enabled this is ALWAYS a variant id (never BASE_TARGET).
    // Defaults to Variant A so Base never flashes into view on load.
    const [activeTarget, setActiveTarget] = useState<string>(() => deriveInitialTarget(initialValues));

    // Tracks whether ANY variant has been modified since the last save.
    // Unlike form.formState.isDirty, this is NOT reset when we call form.reset()
    // during a variant switch — it survives the switch and stays true until
    // the user explicitly saves or discards.
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

    // Tracks whether AB was enabled in the last PERSISTED document (as opposed
    // to `abEnabled`, which reflects live/local state). Callers use this to
    // tell apart "AB just turned on locally, never saved" from "AB is a real,
    // already-saved experiment" — the two need very different "stop" flows.
    const [savedAbEnabled, setSavedAbEnabled] = useState<boolean>(() => initialValues.ab_config?.enable === 1);

    // ------------------------------------------------------------------------
    // Variant cache  (base doc + all variant docs)
    // ------------------------------------------------------------------------
    const cacheRef = useRef<VariantCache<TDoc>>(new Map());

    const buildInitialCache = (): VariantCache<TDoc> => {
        const map: VariantCache<TDoc> = new Map();
        // Base document (hidden once AB is enabled, but always present)
        map.set(BASE_TARGET, initialValues);

        // Pre-populate variant documents from existing overrides
        const abCfg = initialValues.ab_config;
        if (abCfg?.enable === 1 && abCfg.variants?.length) {
            for (const v of abCfg.variants) {
                const doc = buildVariantValues(initialValues, (v.overrides ?? {}) as DeepPartial<TDoc>, skipKeys);
                map.set(v.id, doc);
            }
        }
        return map;
    };

    if (cacheRef.current.size === 0) {
        cacheRef.current = buildInitialCache();
    }

    // ------------------------------------------------------------------------
    // React Hook Form – ONE instance
    // ------------------------------------------------------------------------
    const form = useForm<TDoc>({
        resolver,
        // Start on whatever activeTarget resolved to (Base, or Variant A if AB is on)
        // so the form never briefly shows Base when AB is already enabled.
        defaultValues: (cacheRef.current.get(activeTarget) ?? cacheRef.current.get(BASE_TARGET)) as any,
        mode: "onBlur",
    });

    // ------------------------------------------------------------------------
    // Internal helpers
    // ------------------------------------------------------------------------

    /** Is this target Variant A — i.e. the stand-in for Base while AB is on? */
    const isPrimaryVariant = useCallback(
        (targetId: string) => abEnabled && variants.length > 0 && variants[0].id === targetId,
        [abEnabled, variants],
    );

    /**
     * Flush current form values into cache for the given target.
     *
     * Special case: if `targetId` is Variant A, its skip-key values are
     * written THROUGH to the hidden base document, since Variant A is the
     * user-facing stand-in for Base and owns skip keys while AB is enabled.
     */
    const flushToCache = useCallback(
        (targetId: string) => {
            const values = form.getValues();
            cacheRef.current.set(targetId, values);

            if (isPrimaryVariant(targetId)) {
                const baseDoc = cacheRef.current.get(BASE_TARGET);
                if (baseDoc) {
                    const updatedBase = { ...baseDoc };
                    for (const key of skipKeys) {
                        updatedBase[key] = values[key];
                    }
                    cacheRef.current.set(BASE_TARGET, updatedBase);
                }
            }
        },
        [form, isPrimaryVariant, skipKeys],
    );

    /**
     * Skip keys must mirror the hidden base document on every non-primary
     * variant (B, C, ...) — Variant A is exempt, it OWNS skip keys (see
     * flushToCache above) and must never be overwritten from base.
     *
     * Returns `doc` patched with base's current skip-key values. Must be
     * applied to a document BEFORE it's handed to `form.reset()`, never via
     * `form.setValue()` after — setValue only fixes the live value, leaving
     * reset's defaultValues baseline stuck on whatever was stale in the
     * cache, which then reads as a phantom dirty field the next time RHF
     * recomputes dirty state (e.g. on another tab switch).
     */
    const enforceSkipKeys = useCallback(
        (targetId: string, doc: TDoc): TDoc => {
            if (targetId === BASE_TARGET || isPrimaryVariant(targetId)) return doc;
            const baseDoc = cacheRef.current.get(BASE_TARGET);
            if (!baseDoc) return doc;
            const patched = { ...doc };
            for (const key of skipKeys) {
                patched[key] = baseDoc[key];
            }
            return patched;
        },
        [isPrimaryVariant, skipKeys],
    );

    // ------------------------------------------------------------------------
    // Public API
    // ------------------------------------------------------------------------

    /**
     * Switch editing target.
     * 1. Reject switching to Base while AB is enabled (Base is hidden — Variant A
     *    is the stand-in).
     * 2. Validate current form; block if errors.
     * 3. Flush current form to cache (this also syncs skip keys to base if
     *    leaving Variant A).
     * 4. Reset form with cached values for next target.
     * 5. Enforce skip keys if switching to a non-primary variant.
     */
    const switchVariant = useCallback(
        async (nextTarget: string): Promise<boolean> => {
            if (nextTarget === activeTarget) return true;

            if (abEnabled && nextTarget === BASE_TARGET) {
                console.warn(
                    "[useVariantEditor] Base is hidden while AB testing is enabled; select a variant instead.",
                );
                return false;
            }

            const valid = await form.trigger();
            if (!valid) return false;

            // Persist dirty flag BEFORE the reset wipes it
            if (form.formState.isDirty) {
                setHasUnsavedChanges(true);
            }

            flushToCache(activeTarget);

            const nextDoc = cacheRef.current.get(nextTarget);
            if (!nextDoc) {
                console.warn(`[useVariantEditor] No cache found for target: ${nextTarget}`);
                return false;
            }

            // reset() clears isDirty — but hasUnsavedChanges survives the switch
            form.reset(enforceSkipKeys(nextTarget, nextDoc) as any, { keepDirty: false });
            setActiveTarget(nextTarget);

            return true;
        },
        [abEnabled, activeTarget, flushToCache, form, enforceSkipKeys],
    );

    /**
     * Enable A/B testing.
     * AB testing requires a MINIMUM of 2 variants, so this seeds both
     * Variant A and Variant B at once (both cloned from the current base
     * values), then immediately switches the visible editing target to
     * Variant A — Base becomes hidden.
     */
    const enableAB = useCallback(() => {
        if (abEnabled) return;

        flushToCache(BASE_TARGET);

        const baseDoc = cacheRef.current.get(BASE_TARGET)!;
        const variantAId = nanoid();
        const variantBId = nanoid();

        const newVariants: EditorVariant[] = [{ id: variantAId }, { id: variantBId }];

        const traffic_percentage = { [variantAId]: 50, [variantBId]: 50 };
        const abCfgUpdate: AbConfig = {
            enable: 1,
            exp_id: baseDoc.ab_config?.exp_id ?? nanoid(),
            traffic_allocation_type: "even",
            traffic_percentage,
            variants: [
                { id: variantAId, status: "active", overrides: {} },
                { id: variantBId, status: "active", overrides: {} },
            ],
        };

        const variantADoc = structuredClone(baseDoc);
        const variantBDoc = structuredClone(baseDoc);
        variantADoc.ab_config = abCfgUpdate;
        variantBDoc.ab_config = abCfgUpdate;
        cacheRef.current.get(BASE_TARGET)!.ab_config = abCfgUpdate;

        cacheRef.current.set(variantAId, variantADoc);
        cacheRef.current.set(variantBId, variantBDoc);

        setVariants(newVariants);
        setAbEnabled(true);
        setActiveTarget(variantAId);
        form.reset(variantADoc as any, { keepDirty: false });
        setHasUnsavedChanges(true);
    }, [abEnabled, flushToCache, form]);

    /**
     * Disable A/B testing locally (no backend call — this only makes sense
     * for AB setups that were never saved; a persisted experiment must be
     * stopped by picking a winner instead, see `markAbTestWinner`).
     *
     * Collapses the variant cache back down to a single base document, using
     * Variant A's doc as that base (it's the user-facing stand-in for Base
     * while AB is on — see the architecture comment at the top of this file).
     */
    const disableAB = useCallback(() => {
        if (!abEnabled || variants.length === 0) return;

        flushToCache(activeTarget);

        const primaryDoc = cacheRef.current.get(variants[0].id)!;
        const newBase: TDoc = {
            ...primaryDoc,
            ab_config: { ...(primaryDoc.ab_config ?? { enable: 0 as const }), enable: 0 as const, variants: undefined },
        };

        cacheRef.current.clear();
        cacheRef.current.set(BASE_TARGET, newBase);

        setVariants([]);
        setAbEnabled(false);
        setActiveTarget(BASE_TARGET);
        form.reset(newBase as any, { keepDirty: false });
        setHasUnsavedChanges(true);
    }, [abEnabled, activeTarget, flushToCache, form, variants]);

    /**
     * Add a new variant (clone of base).
     * Label is auto-generated: "Variant A", "Variant B", ...
     */
    const addVariant = useCallback(() => {
        if (!abEnabled) return;

        flushToCache(activeTarget);

        const baseDoc = structuredClone(cacheRef.current.get(BASE_TARGET)!);
        const variantId = nanoid();

        const newVariant: EditorVariant = { id: variantId };

        const updatedVariants = [...variants, newVariant];
        const existingAb = baseDoc.ab_config ?? { enable: 1 as const };
        const newTraffic = recalculateTraffic("even", updatedVariants, existingAb.traffic_percentage ?? {});
        const newAbCfg: AbConfig = {
            ...existingAb,
            traffic_allocation_type: "even",
            traffic_percentage: newTraffic,
        };

        // Update ab_config on all cached docs
        for (const [, doc] of cacheRef.current) {
            doc.ab_config = newAbCfg;
        }
        baseDoc.ab_config = newAbCfg;
        cacheRef.current.set(variantId, baseDoc);
        setVariants(updatedVariants);
        setHasUnsavedChanges(true);
    }, [abEnabled, activeTarget, flushToCache, variants]);

    /**
     * Remove a variant.
     *
     * AB testing requires a MINIMUM of 2 variants at all times, so removal
     * is refused once only 2 remain (the UI should hide the remove control
     * in that case too — but this guard is the source of truth).
     *
     * - If a non-active variant is removed, nothing else changes.
     * - If the ACTIVE variant is removed, and other variants remain, switch
     *   to the new first variant.
     *     - IMPORTANT: "Variant A" is a POSITIONAL role (`variants[0]`), not
     *       a name. So if Variant A is deleted while Variant B and C exist,
     *       B automatically becomes `variants[0]` — meaning `isPrimaryVariant`
     *       now returns true for B, so B stops having its skip keys
     *       overwritten from base (via enforceSkipKeys) and instead starts
     *       writing its own skip-key edits through to base (via
     *       flushToCache). No extra code is needed for this hand-off; it
     *       falls out of the positional check automatically.
     */
    const removeVariant = useCallback(
        async (variantId: string) => {
            if (variants.length <= 2) {
                console.warn("[useVariantEditor] AB testing requires a minimum of 2 variants; cannot remove further.");
                return;
            }

            if (activeTarget === variantId) {
                flushToCache(activeTarget);
            }

            cacheRef.current.delete(variantId);
            const remaining = variants.filter((v) => v.id !== variantId);

            // Re-normalise traffic across remaining variants
            const baseDoc = cacheRef.current.get(BASE_TARGET)!;
            const existingAb = baseDoc.ab_config ?? { enable: 0 as const };
            const newTraffic = recalculateTraffic("even", remaining, existingAb.traffic_percentage ?? {});
            const newAbCfg: AbConfig = {
                ...existingAb,
                traffic_allocation_type: "even",
                traffic_percentage: newTraffic,
            };
            for (const [, doc] of cacheRef.current) {
                doc.ab_config = newAbCfg;
            }

            setVariants(remaining);

            if (activeTarget === variantId) {
                if (remaining.length > 0) {
                    const nextTarget = remaining[0].id;
                    const nextDoc = cacheRef.current.get(nextTarget)!;
                    form.reset(enforceSkipKeys(nextTarget, nextDoc) as any, { keepDirty: false });
                    setActiveTarget(nextTarget);
                } else {
                    const nextBase = cacheRef.current.get(BASE_TARGET)!;
                    form.reset(nextBase as any, { keepDirty: false });
                    setActiveTarget(BASE_TARGET);
                }
            }

            if (remaining.length === 0) {
                setAbEnabled(false);
            }
            setHasUnsavedChanges(true);
        },
        [activeTarget, enforceSkipKeys, flushToCache, form, variants],
    );

    /**
     * Rename a variant.
     */
    const renameVariant = useCallback((variantId: string, name: string) => {
        setVariants((prev) => prev.map((v) => (v.id === variantId ? { ...v, name } : v)));
    }, []);

    /**
     * Update traffic allocation settings from the A/B Settings modal.
     * Propagates the new ab_config across all variant cache docs and into RHF.
     */
    const updateABConfig = useCallback(
        (allocationType: "even" | "custom", customPercentages?: Record<string, number>) => {
            const baseDoc = cacheRef.current.get(BASE_TARGET)!;
            const existingAb = baseDoc.ab_config ?? { enable: 1 as const };

            const traffic_percentage =
                allocationType === "even"
                    ? recalculateTraffic("even", variants, {})
                    : recalculateTraffic("custom", variants, customPercentages ?? existingAb.traffic_percentage ?? {});

            const newAbCfg: AbConfig = {
                ...existingAb,
                traffic_allocation_type: allocationType,
                traffic_percentage,
            };

            for (const [, doc] of cacheRef.current) {
                doc.ab_config = newAbCfg;
            }

            form.setValue("ab_config" as any, newAbCfg as any, { shouldDirty: true });
            setHasUnsavedChanges(true);
        },
        [form, variants],
    );

    /**
     * Validate every variant's cached document against `resolver`, not just
     * whatever happens to be loaded into the live RHF form.
     *
     * `form.trigger()` (and thus `switchVariant`) only ever validates the
     * ACTIVE target — a variant that was never switched to (e.g. Variant B,
     * left untouched after `enableAB` seeds it) never gets schema-checked at
     * all. Without this, an invalid/incomplete variant document can be
     * silently included in `buildPayload()`'s output.
     *
     * Returns the id of the first invalid target found, or `null` if every
     * variant (including the currently active one) is valid.
     */
    const validateAllTargets = useCallback(async (): Promise<string | null> => {
        flushToCache(activeTarget);

        const targets = abEnabled ? variants.map((v) => v.id) : [BASE_TARGET];
        for (const targetId of targets) {
            const doc = cacheRef.current.get(targetId);
            if (!doc) continue;
            const result = await resolver(doc as any, undefined, {
                fields: {},
                shouldUseNativeValidation: false,
                criteriaMode: "all",
            } as any);
            if (result.errors && Object.keys(result.errors).length > 0) {
                return targetId;
            }
        }
        return null;
    }, [abEnabled, activeTarget, flushToCache, resolver, variants]);

    /**
     * Make `targetId` the active editing target without validating whatever
     * is currently active first (unlike `switchVariant`). Intended for
     * jumping straight to a variant that `validateAllTargets` flagged as
     * invalid, so its errors can be surfaced via `form.trigger()`.
     */
    const jumpToTarget = useCallback(
        (targetId: string) => {
            const nextDoc = cacheRef.current.get(targetId);
            if (!nextDoc) return;
            form.reset(enforceSkipKeys(targetId, nextDoc) as any, { keepDirty: false });
            setActiveTarget(targetId);
        },
        [form, enforceSkipKeys],
    );

    /**
     * Build the final payload to send to the backend.
     *
     * 1. Flush current form values into cache (syncs skip keys to base if
     *    the active target is Variant A).
     * 2. Base doc = cacheRef.current.get("base").
     * 3. For each variant (including Variant A): diff(base, variantDoc) → overrides.
     * 4. Assemble payload with ab_config.
     */
    const buildPayload = useCallback((): TDoc => {
        flushToCache(activeTarget);

        const baseDoc = cacheRef.current.get(BASE_TARGET)!;
        const existingAb = baseDoc.ab_config ?? { enable: 0 as const };

        if (!abEnabled || variants.length === 0) {
            return { ...baseDoc, ab_config: { ...existingAb, enable: 0 } } as TDoc;
        }

        const abVariants: AbConfigVariant[] = variants.map((v) => {
            const variantDoc = cacheRef.current.get(v.id)!;
            const overrides = buildOverrides(baseDoc, variantDoc, skipKeys);
            return {
                id: v.id,
                status: "active",
                overrides: overrides as Record<string, unknown>,
            };
        });

        return {
            ...baseDoc,
            ab_config: {
                ...existingAb,
                enable: 1,
                traffic_allocation_type: existingAb.traffic_allocation_type ?? "even",
                traffic_percentage: existingAb.traffic_percentage ?? recalculateTraffic("even", variants, {}),
                variants: abVariants,
            },
        } as TDoc;
    }, [abEnabled, activeTarget, flushToCache, skipKeys, variants]);

    /**
     * Save handler.
     * 1. Trigger full form validation.
     * 2. Build payload.
     * 3. Call onSave.
     * 4. Mark form as clean.
     */
    const handleSave = useCallback(async () => {
        const valid = await form.trigger();
        if (!valid) return;

        const payload = buildPayload();
        await onSave(payload);

        // After save, mark everything clean globally
        setHasUnsavedChanges(false);
        form.reset(form.getValues());
    }, [buildPayload, form, onSave]);

    const lastSavedConfigRef = useRef<TDoc>(initialValues);

    /**
     * Patch a single top-level field across EVERY cached document (base +
     * all variants), the last-saved snapshot, and the live form.
     *
     * For fields edited normally through the form, only the active target
     * changes and everything else is diffed into per-variant `overrides` on
     * save — that's the whole point of the variant cache. But some fields
     * (e.g. `status`, toggled via a dedicated publish/unpublish endpoint
     * that isn't part of the save flow) are updated out-of-band and must
     * never diverge between variants. Without this, only the cache entry
     * for whatever target happened to be active at toggle time gets the new
     * value — every sibling variant keeps serving the stale value the next
     * time it's switched to.
     *
     * `value` may also be an updater function `(prevValue) => nextValue`,
     * evaluated separately per document (React-setState style) — needed for
     * fields whose value is itself per-variant (e.g. a nested object where
     * only one sub-property should be forced to match across variants).
     * Passing a plain value keeps the old flat "same value everywhere"
     * behavior. // Bug Fix 1
     */
    const patchAllTargets = useCallback(
        <K extends keyof TDoc>(key: K, value: TDoc[K] | ((prev: TDoc[K]) => TDoc[K])) => {
            const isUpdater = typeof value === "function";
            for (const [, doc] of cacheRef.current) {
                doc[key] = isUpdater ? (value as (prev: TDoc[K]) => TDoc[K])(doc[key]) : value;
            }
            const activeDoc = cacheRef.current.get(activeTarget);
            const nextFormValue = activeDoc ? activeDoc[key] : isUpdater ? undefined : value;
            lastSavedConfigRef.current = {
                ...lastSavedConfigRef.current,
                [key]: isUpdater ? (value as (prev: TDoc[K]) => TDoc[K])(lastSavedConfigRef.current[key]) : value,
            };
            form.setValue(key as any, nextFormValue as any, { shouldDirty: false });
            form.reset(form.getValues(), { keepDirty: false });
        },
        [form, activeTarget],
    );

    /**
     * Debug helper: field-level diff of every cached target (base + all
     * variants) against what was actually last saved, keyed by target id.
     * Unlike `form.formState.dirtyFields` (which only covers whichever
     * target is currently loaded into the form), this covers every variant,
     * including ones the user edited and then switched away from — which is
     * exactly what `hasUnsavedChanges` tracks as a boolean without detail.
     *
     * Flushes the active target into the cache first so its live in-progress
     * edits are included, not just whatever was cached at the last switch.
     */
    const getUnsavedChangesDetail = useCallback((): Record<string, Record<string, unknown>> => {
        flushToCache(activeTarget);

        const savedBase = lastSavedConfigRef.current;
        const savedOverridesByTarget = new Map(
            (savedBase.ab_config?.variants ?? []).map((v) => [v.id, v.overrides ?? {}]),
        );

        const result: Record<string, Record<string, unknown>> = {};
        for (const [target, currentDoc] of cacheRef.current) {
            const savedDoc =
                target === BASE_TARGET
                    ? savedBase
                    : buildVariantValues(
                          savedBase,
                          (savedOverridesByTarget.get(target) ?? {}) as DeepPartial<TDoc>,
                          skipKeys,
                      );
            const diff = diffObjects(savedDoc as any, currentDoc as any);
            if (diff) {
                result[target] = diff;
            }
        }
        return result;
    }, [activeTarget, flushToCache, skipKeys]);

    /**
     * Debug helper: validates every cached target (base + all variants)
     * against `resolver` — same targets `validateAllTargets` checks, except
     * it doesn't stop at the first invalid one and doesn't discard *which*
     * keys failed. Errors come back flattened to `"path.to.field": message`
     * per target (see `flattenFieldErrors`), so a nested error (e.g. inside
     * `general_settings.title`) is legible instead of buried in RHF's nested
     * error tree.
     *
     * Also reports why the unsaved-changes save bar is showing. The bar's
     * "has changes" condition is `form.formState.isDirty || hasUnsavedChanges`
     * (see BundleBuilderFormPreviewWrapper) — two INDEPENDENT signals:
     *   - `hasUnsavedChanges` / `unsavedChangesByTarget` come from this
     *     engine's own cache-vs-lastSaved diff (see `getUnsavedChangesDetail`).
     *   - `isDirty` / `dirtyFields` come straight from RHF's own defaultValues
     *     comparison on the currently active target, which `cacheRef`-based
     *     diffing can't see — e.g. a field some component wrote via
     *     `setValue(..., { shouldDirty: true })` on mount, before the user
     *     touched anything. `dirtyFields` is RHF's nested map of exactly
     *     which keys it thinks changed, so it's the fastest way to spot that.
     *
     * `dirtyFields` only gets populated for paths RHF's OWN tracked
     * `setValue(..., { shouldDirty: true })` / `Controller.onChange` path
     * walks — `isDirty` itself is a full deep-equal of the whole values tree
     * against `formState.defaultValues`, so a diff living in untracked
     * "passenger" data (e.g. `products`, opaque arrays/objects nobody calls
     * `setValue` on individually) flips `isDirty` while leaving `dirtyFields`
     * empty. `isDirtyDiff` re-runs that same deep-equal RHF itself uses (see
     * `diffAgainstDefaultValues` above — a plain bracket-access diff isn't
     * enough here: RHF's own equality check treats a key that's PRESENT with
     * `undefined` on one side and genuinely ABSENT on the other as a real
     * difference, which bracket access can't see) and returns the actual
     * field paths + before/after values responsible, so this case is
     * diagnosable even when `dirtyFields` comes back empty.
     *
     * Flushes the active target into the cache first so its live
     * in-progress edits are included.
     */
    const getValidationErrorsDetail = useCallback(async (): Promise<{
        hasUnsavedChanges: boolean;
        unsavedChangesByTarget: Record<string, Record<string, unknown>>;
        validationErrorsByTarget: Record<string, Record<string, string>>;
        isDirty: boolean;
        dirtyFields: unknown;
        isDirtyDiff: Record<string, { defaultValue: unknown; currentValue: unknown }>;
    }> => {
        flushToCache(activeTarget);

        const targets = abEnabled ? variants.map((v) => v.id) : [BASE_TARGET];
        const validationErrorsByTarget: Record<string, Record<string, string>> = {};

        for (const targetId of targets) {
            const doc = cacheRef.current.get(targetId);
            if (!doc) continue;
            const result = await resolver(doc as any, undefined, {
                fields: {},
                shouldUseNativeValidation: false,
                criteriaMode: "all",
            } as any);
            const fieldErrors = flattenFieldErrors(result.errors);
            if (Object.keys(fieldErrors).length > 0) {
                validationErrorsByTarget[targetId] = fieldErrors;
            }
        }

        return {
            hasUnsavedChanges,
            unsavedChangesByTarget: getUnsavedChangesDetail(),
            validationErrorsByTarget,
            isDirty: form.formState.isDirty,
            dirtyFields: form.formState.dirtyFields,
            isDirtyDiff: diffAgainstDefaultValues(form.formState.defaultValues, form.getValues()),
        };
    }, [abEnabled, activeTarget, flushToCache, form, getUnsavedChangesDetail, hasUnsavedChanges, resolver, variants]);

    /**
     * Fully initialise the editor from a freshly-fetched document.
     *
     * - Rebuilds the variant cache for base + all A/B variants.
     * - Resets the RHF form to Variant A (or Base, if AB isn't enabled).
     * - Re-derives the variant list state.
     *
     * Call this instead of a bare `form.reset()` after every fetch so that
     * switchVariant works correctly for saved A/B bundles.
     */
    const initializeEditor = useCallback(
        (fetchedConfig: TDoc) => {
            // Keep the fetched configuration as the discard fallback
            lastSavedConfigRef.current = fetchedConfig;

            // 1. Clear and rebuild the cache
            cacheRef.current.clear();

            const baseDoc = { ...fetchedConfig };
            cacheRef.current.set(BASE_TARGET, baseDoc);

            // 2. Rebuild variant documents from stored overrides
            const abCfg = fetchedConfig.ab_config;
            const hasAB = abCfg?.enable === 1 && Array.isArray(abCfg.variants) && abCfg.variants.length > 0;

            const newVariants: EditorVariant[] = [];

            if (hasAB) {
                for (const v of abCfg!.variants!) {
                    const variantDoc = buildVariantValues(
                        fetchedConfig,
                        (v.overrides ?? {}) as DeepPartial<TDoc>,
                        skipKeys,
                    );
                    cacheRef.current.set(v.id, variantDoc);
                    newVariants.push({ id: v.id });
                }
            }

            // 3. Update React state — default to Variant A when AB is on, so
            //    Base never becomes the visible target.
            const initialTarget = hasAB ? newVariants[0].id : BASE_TARGET;
            setVariants(newVariants);
            setAbEnabled(hasAB);
            setSavedAbEnabled(hasAB);
            setActiveTarget(initialTarget);
            setHasUnsavedChanges(false);

            // 4. Reset the RHF form to the resolved initial target
            const initialDoc = cacheRef.current.get(initialTarget) as TDoc;
            form.reset(initialDoc as any);
        },
        [form, skipKeys],
    );

    /**
     * Applies a bundle document as-is — e.g. the response from "set winner",
     * which already has ab_config stripped and the winning variant's
     * overrides merged in server-side. Unlike `initializeEditor`, this does
     * NOT read `skipKeys`/overrides back out of an `ab_config` (there isn't
     * one anymore); it just replaces the entire cache/form with exactly what
     * was handed in, with no merge against whatever was there before, and
     * marks the form clean.
     */
    const applyWinnerBundle = useCallback(
        (bundle: TDoc) => {
            const cleanBundle = { ...bundle, ab_config: undefined };
            cacheRef.current.clear();
            cacheRef.current.set(BASE_TARGET, cleanBundle);
            lastSavedConfigRef.current = cleanBundle;

            setVariants([]);
            setAbEnabled(false);
            setSavedAbEnabled(false);
            setActiveTarget(BASE_TARGET);
            setHasUnsavedChanges(false);

            form.reset(cleanBundle as any, { keepDirty: false });
        },
        [form],
    );

    /**
     * Refreshes the last saved state ref when a save successfully completes.
     *
     * `config` is the BASE document (base + ab_config, as actually persisted).
     * It must never be handed to `form.reset()` directly: while AB is
     * enabled, `activeTarget` is a variant, and the visible form has to keep
     * showing that variant's merged view (base + its own overrides), not
     * base itself — otherwise the UI flashes base's values right after every
     * save, until the next full reload rebuilds the cache correctly via
     * initializeEditor.
     */
    const updateLastSavedConfig = useCallback(
        (config: TDoc) => {
            lastSavedConfigRef.current = config;
            setHasUnsavedChanges(false);
            setSavedAbEnabled(config.ab_config?.enable === 1);

            // Keep the base cache entry in sync with what was actually persisted.
            cacheRef.current.set(BASE_TARGET, config);

            const activeDoc = (cacheRef.current.get(activeTarget) ?? config) as any;
            activeDoc.form_id = (config as any).form_id;
            form.reset(activeDoc);
        },
        [form, activeTarget],
    );

    /**
     * Clear and rebuild the cache from the last known saved/fetched state.
     */
    const resetCache = useCallback(() => {
        cacheRef.current.clear();
        const baseDoc = lastSavedConfigRef.current;
        const abCfg = baseDoc.ab_config;
        const initVariants =
            abCfg?.enable === 1 && abCfg.variants?.length ? abCfg.variants.map((v) => ({ id: v.id })) : [];
        setVariants(initVariants);
        setAbEnabled(abCfg?.enable === 1);
        setSavedAbEnabled(abCfg?.enable === 1);
        setHasUnsavedChanges(false);

        // Re-populate variant documents from existing overrides
        cacheRef.current.set(BASE_TARGET, baseDoc);
        if (abCfg?.enable === 1 && abCfg.variants?.length) {
            for (const v of abCfg.variants) {
                const doc = buildVariantValues(baseDoc, (v.overrides ?? {}) as DeepPartial<TDoc>, skipKeys);
                cacheRef.current.set(v.id, doc);
            }
        }

        // Default to Variant A when AB is on, so Base never becomes visible.
        const initialTarget = abCfg?.enable === 1 && initVariants.length > 0 ? initVariants[0].id : BASE_TARGET;
        setActiveTarget(initialTarget);

        const initialDoc = cacheRef.current.get(initialTarget) as TDoc;
        form.reset(initialDoc as any);
    }, [form, skipKeys]);

    useEffect(() => {
        if (initialValues) {
            lastSavedConfigRef.current = initialValues;
            resetCache();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues, resetCache]);

    useEffect(() => {
        window.__getValidationErrorsDetail = getValidationErrorsDetail;
        return () => {
            delete window?.__getValidationErrorsDetail;
        };
    }, [getValidationErrorsDetail]);

    return {
        form,
        activeTarget,
        abEnabled,
        savedAbEnabled,
        // `variants` never includes Base — this is already the "visible tabs"
        // list. When abEnabled is true, render ONLY these as tabs. When false,
        // this array will be empty and you should render just the Base tab.
        variants,
        cacheRef,
        hasUnsavedChanges,
        initializeEditor,
        applyWinnerBundle,
        updateLastSavedConfig,
        switchVariant,
        enableAB,
        disableAB,
        addVariant,
        removeVariant,
        renameVariant,
        updateABConfig,
        buildPayload,
        validateAllTargets,
        jumpToTarget,
        handleSave,
        resetCache,
        patchAllTargets,
    };
}

export type UseVariantEditorReturn<TDoc extends object & { ab_config?: AbConfig }> = ReturnType<
    typeof useVariantEditor<TDoc>
>;
